import { useTranslation } from 'i18n'
import React, { ChangeEvent, forwardRef, useMemo, useState } from 'react'
import { useSTheme } from 'styles/theme'
import { useFormContext, ValidationRules } from 'react-hook-form'
import ConditionalRender from 'utils/conditionalRender'
import clsx from 'clsx'
import LockSvg from 'assets/svgs/locked.svg'

import XText from 'v2/components/XText'

import styles from './style.module.scss'
import { BUTTON_STYLE_TYPE, XTEXT_TYPE } from 'v2/styles/constants'
import XInputError from '../XInputError'
import XColors from 'v2/styles/colors'
import XButton from '../XButton'
import { ga4_inputChanged } from 'utils/ga4'
import STooltip from 'components/opinionless/STooltip'

interface Props {
    id: string
    name: string
    alwaysDatePicker?: boolean
    datePicker?: boolean
    type: XInputType
    label: string
    readOnly?: boolean
    noMargin?: boolean
    smallerHeight?: boolean
    fullParent?: boolean
    placeholder?: string
    validators?: ValidationRules
    value?: string | number
    dropDownValue?: string
    children?: any
    initialValue?: any
    tooltip?: any
    showPasswordToggler?: () => void
    maxLength?: number
    onClick?: () => void
    onChange?: (e: ChangeEvent) => void
    icon?: any
}

export const XInputTypeEnum = {
    TEXT: { type: 'xinput__text', class: 'text' },
    LARGE_TEXT: { type: 'xinput__large_text', class: 'textarea' },
    PASSWORD: { type: 'xinput__password', class: 'password' },
    NUMBER: { type: 'xinput__number', class: 'number' },
    MONEY: { type: 'xinput__money', class: 'money' },
    EMAIL: { type: 'xinput__email', class: 'email' },
    DROPDOWN: { type: 'xinput__dropdown', class: 'dropdown' },
}

type XInputType = typeof XInputTypeEnum[keyof typeof XInputTypeEnum]

const XInput = forwardRef<any, Props>((props: Props, ref) => {
    const {
        id,
        name,
        label,
        placeholder,
        readOnly,
        type,
        value,
        dropDownValue,
        noMargin,
        smallerHeight,
        icon,
        tooltip,
        ...otherProps
    } = props

    const { errors } = useFormContext()
    const theme = useSTheme()
    const { t } = useTranslation(window.LANG_NS)
    const [showPassword, setShowPassword] = useState(false)

    const showPasswordToggler = () => {
        setShowPassword(!showPassword)
    }
    const computedStyles = useMemo(() => {
        let computedStyles = [
            styles.xInput__field,
            props.datePicker ? styles.xInput__field__pointer : styles.xInput__field__initial,
        ]
        computedStyles = type === XInputTypeEnum.LARGE_TEXT ? [styles.xInput__large_text] : computedStyles

        computedStyles =
            readOnly && type !== XInputTypeEnum.DROPDOWN
                ? [...computedStyles, styles.xInput__field__read_only]
                : computedStyles

        computedStyles = errors[name]?.message ? [...computedStyles, styles.xInput__field__error] : computedStyles
        computedStyles =
            type === XInputTypeEnum.DROPDOWN && props.value !== props.initialValue
                ? [...computedStyles, styles.xInput__field__dropdown]
                : computedStyles
        computedStyles = noMargin ? [...computedStyles, styles.xInput__field__no_margin] : computedStyles
        computedStyles = smallerHeight ? [...computedStyles, styles.xInput__field__smaller_height] : computedStyles

        return computedStyles
    }, [errors[name]?.message, name])

    const textAreaComputedStyles = useMemo(() => {
        let textAreaComputedStyles = [styles.xInput__field__large_text]
        textAreaComputedStyles = errors[name]?.message
            ? [...textAreaComputedStyles, styles.xInput__field__error]
            : textAreaComputedStyles
        return textAreaComputedStyles
    }, [errors[name]?.message, name])

    const fieldType = useMemo(() => {
        return type === XInputTypeEnum.PASSWORD && showPassword
            ? XInputTypeEnum.TEXT.class
            : XInputTypeEnum.PASSWORD.class
    }, [showPassword])
    return (
        <div className={clsx(styles.xInput, noMargin ? styles.xInput__field__no_margin : null)}>
            <div className={styles.xInput__label}>
                <ConditionalRender if={label}>
                    <XText color={XColors.midGray} type={XTEXT_TYPE.LABEL}>
                        {label}
                    </XText>
                </ConditionalRender>
                <ConditionalRender if={props.tooltip}>
                    <div className={styles.xInput__tooltip}>
                        <STooltip content={props.tooltip} />
                    </div>
                </ConditionalRender>
            </div>

            <div className={clsx(styles.xInput__container)}>
                {type === XInputTypeEnum.LARGE_TEXT ? (
                    <textarea className={clsx(...textAreaComputedStyles)} ref={ref} id={id} name={id}></textarea>
                ) : (
                    <input
                        id={id}
                        name={id}
                        className={clsx(...computedStyles)}
                        type={
                            type === XInputTypeEnum.PASSWORD
                                ? fieldType
                                : props.type.class || XInputTypeEnum.LARGE_TEXT.class
                        }
                        readOnly={readOnly}
                        placeholder={placeholder}
                        defaultValue={props.initialValue}
                        onChange={(e) => {
                            props.onChange?.(e)
                            ga4_inputChanged(id, type === XInputTypeEnum.DROPDOWN || e.target.value.length === 1)
                        }}
                        onClick={props.onClick}
                        maxLength={props.maxLength}
                        ref={ref}
                        {...(props.datePicker && otherProps)}
                        style={{ fontFamily: 'IBM Plex Sans' }}
                    />
                )}
                <ConditionalRender if={type === XInputTypeEnum.PASSWORD}>
                    <XButton
                        rightIcon={'visibility'}
                        styleType={BUTTON_STYLE_TYPE.PASSWORD}
                        onClick={showPasswordToggler}
                    />
                </ConditionalRender>

                <ConditionalRender if={readOnly && type != XInputTypeEnum.DROPDOWN}>
                    <LockSvg className={clsx(styles.xInput__lockImage)} />
                </ConditionalRender>

                <ConditionalRender if={icon}>
                    <div className={clsx(styles.xInput__icon)}>{icon}</div>
                </ConditionalRender>

                <ConditionalRender if={errors[name]?.message}>
                    <XInputError id={id + '-error'} forElementId={id}>
                        {t(errors[name]?.message)}
                    </XInputError>
                </ConditionalRender>
            </div>
        </div>
    )
})

XInput.displayName = 'XInput'

export default XInput
